import { injectable } from "inversify";
import { FormFields } from "features/shared/forms/hooks/useForm";
import IReservedCapacityGasFormService from "./IReservedCapacityGasFormService";
import { add, addDays, addMonths, getMonth, isAfter, isEqual, isFirstDayOfMonth, isWithinInterval, lastDayOfMonth, startOfDay } from "date-fns";
import { Nullable } from "features/shared/types/nullable";
import IRepository from "data/IRepository";
import ReservedCapacityGasPeriodFromDTO from "features/requirements/dtos/ReservedCapacityGasPeriodFromDTO";
import { inversifyContainer } from "infrastructure/ioc/ioc.container";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { getQueryParams } from "features/shared/helpers/queryParams";
import { Requirements_ReservedCapacityGasTimeInterval } from "data/endpoints";

@injectable()
export default class ReservedCapacityGasFormService implements IReservedCapacityGasFormService {
  private readonly _gasPeriodRepository: IRepository<ReservedCapacityGasPeriodFromDTO>;
    
  constructor() {
    this._gasPeriodRepository = inversifyContainer.get<IRepository<ReservedCapacityGasPeriodFromDTO>>(IocTypes.Repository);
  }

  getDatesByCapacityType(capacityType: string, periodFrom: ReservedCapacityGasPeriodFromDTO): Nullable<Date>[] {
    switch (capacityType) {
      case "Daily":
        let dailyFromDate = this.getDailyOrMonthlyFromDate(periodFrom);

        return [
          dailyFromDate,
          dailyFromDate !== null ? this.getToDateByFromDate(dailyFromDate, "Daily") : null
        ]; 
      case "Monthly":
        let monthlyFromDate = this.getDailyOrMonthlyFromDate(periodFrom);

        return [
          monthlyFromDate,
          monthlyFromDate !== null ? this.getToDateByFromDate(monthlyFromDate, "Monthly") : null
        ];
      case "Sliding":
        var nextDay = new Date();
        nextDay.setDate(nextDay.getDate() + 1);
        let slidingFromDate = this.getSlidingFromDate(periodFrom);

        return [
          slidingFromDate,
          slidingFromDate !== null ? this.getToDateByFromDate(slidingFromDate, "Sliding") : null
        ]; 
      default:
        return [];
    }
  }

  prepareValuesToSave(values: FormFields): FormFields {
    let newValues = {...values};
    newValues["DESC_TMKAPACITAPLYNPOZADOVANAKAPACITA"] = `${values["DESC_TMKAPACITAPLYNPOZADOVANAKAPACITA"]}`.replace(",", ".");

    if (newValues["DESC_TMKAPACITAPLYNTYP"] == "Daily") {
      delete newValues["DESC_TMKAPACITAPLYNDO"];
    }

    return newValues;
  }

  getDateFromIsOutsideOfRangeFunction(capacityType: string, minFromDate: Nullable<Date>, maxFromDate: Date): (day: Date) => boolean {
    switch (capacityType) {
      case "Daily":
      case "Monthly":
        const dailyIsOutsideOfRangeFn = (day: Date): boolean => {  
          let actualDate = new Date();   
          let minFrom = minFromDate ? new Date(minFromDate) : addMonths(new Date(actualDate.getFullYear(), actualDate.getMonth(), 1), 1);
          let maxFrom = maxFromDate ? new Date(maxFromDate) : new Date("9999-12-31");

          if (isFirstDayOfMonth(day) && isWithinInterval(startOfDay(day), { start: minFrom, end: maxFrom })) {
            return false;
          }

          return true;
        }
        return dailyIsOutsideOfRangeFn;        
      case "Sliding":
        const slidingDateFromIsOutsideOfRange = (day: Date): boolean => { 
          let minFrom = minFromDate ? startOfDay(new Date(minFromDate)) : addDays(startOfDay(new Date()), 1);
          let maxFrom = maxFromDate ? new Date(maxFromDate) : new Date("9999-12-31");

          if (isWithinInterval(startOfDay(day), { start: minFrom, end: maxFrom })) {
            return false;
          }

          return true;
        }
        return slidingDateFromIsOutsideOfRange;
      default:
        const defaultIsOutsideOfRange: (day: Date) => boolean = (day) => false; // default = all days is valid
        return defaultIsOutsideOfRange;
    }
  }

  getDateToIsOutsideOfRangeFunction(capacityType: string, selectedFromDate: Nullable<Date>): (day: Date) => boolean {
    if (capacityType == "Sliding" && selectedFromDate) {
      const slidingDateFromIsOutsideOfRange = (day: Date): boolean => { 
        let lastDayOfNextMonth = this.getLastDateOfNextMonth(selectedFromDate);
        let addMonthMinusOneDay = addDays(addMonths(startOfDay(selectedFromDate), 1), -1);
    
        if (isEqual(startOfDay(day), lastDayOfNextMonth)) {
          return false;
        } else if (isEqual(startOfDay(day), addMonthMinusOneDay)) {
          return false;
        }
    
        return true;
      }
      return slidingDateFromIsOutsideOfRange;
    }

    const defaultIsOutsideOfRange: (day: Date) => boolean = (day) => false; // default = all days is valid
    return defaultIsOutsideOfRange;
  }

  getLastDateOfNextMonth(date: Date): Date {
    let lastDayOfNextMonth = new Date(date.getFullYear(), date.getMonth() + 2, 0);
    return lastDayOfNextMonth;
  }

  getToDateByFromDate(fromDate: Date, capacityType: string): Date {
    switch (capacityType) {
      case "Daily":
        return new Date("2099-12-31");
      case "Monthly":
        return lastDayOfMonth(fromDate);
      case "Sliding":
        return this.getLastDateOfNextMonth(fromDate);
      default:
        return new Date();
    }
  }

  fetchGasPeriodFrom(capacityType: string): Promise<ReservedCapacityGasPeriodFromDTO> {
    return this._gasPeriodRepository.getOne({
      url: Requirements_ReservedCapacityGasTimeInterval,
      params: getQueryParams("CapacityGasType", capacityType),
    });
  }

  private getDailyOrMonthlyFromDate(periodFrom: ReservedCapacityGasPeriodFromDTO):Nullable<Date>
  {
    let actualDate = new Date();
    let firstDayOfNextMonth = new Date(actualDate.getFullYear(), actualDate.getMonth() + 1, 1);

    if (periodFrom.isOK) {
      let periodFromDate = periodFrom.from ? new Date(periodFrom.from) : firstDayOfNextMonth;
      let periodToDate = periodFrom.to ? new Date(periodFrom.to) : new Date("9999-12-31") ;

      if (isWithinInterval(firstDayOfNextMonth, { start: periodFromDate, end: periodToDate })) {
        return firstDayOfNextMonth;
      } else {
        let updatedFirstDayOfNextMonth = periodFromDate.getDate() == 1 ? periodFromDate : new Date(periodFromDate.getFullYear(), periodFromDate.getMonth() + 1, 1);

        if (isWithinInterval(updatedFirstDayOfNextMonth, { start: periodFromDate, end: periodToDate })) {
          return updatedFirstDayOfNextMonth;
        }
      }
    }

    return null;
  }

  private getSlidingFromDate(periodFrom: ReservedCapacityGasPeriodFromDTO):Nullable<Date>
  {
    let nextDay = add(new Date(), { days: 1 });

    if (periodFrom.isOK) {
      let periodFromDate = periodFrom.from ? new Date(periodFrom.from) : nextDay;
      let periodToDate = periodFrom.to ? new Date(periodFrom.to) : new Date("9999-12-31") ;

      if (isWithinInterval(nextDay, { start: periodFromDate, end: periodToDate })) {
        return nextDay;
      } else {
        if (isWithinInterval(periodFromDate, { start: periodFromDate, end: periodToDate })) {
          return periodFromDate;
        }
      }
    }

    return null;
  }
}